/* Sprint FGI-lite — Video Visual Contract Panel.
 *
 * Pure read-only panel mounted inside the Video Studio. Calls
 *   GET /api/projects/{id}/video-visual-contract
 * once on mount and renders one row per storyboard shot with:
 *   - storyboard frame thumbnail
 *   - paired clip thumbnail (or placeholder when missing)
 *   - visual_type / provider_reference_mode / continuity_risk chips
 *   - duration target vs provider chip
 *   - forbidden / required-subject summary
 *   - reference checklist
 *   - operator-facing honesty banner.
 *
 * Soft guidance only. No Run buttons added. Soft chips, never blocks.
 */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import {
    Film, RefreshCw, AlertTriangle, ShieldOff, Image as ImageIcon,
    Package, MapPin, User2,
} from "lucide-react";

const VISUAL_TYPE_STYLE = {
    character_led: "border-violet-500/40 bg-violet-500/10 text-violet-200",
    environment_only: "border-emerald-500/40 bg-emerald-500/10 text-emerald-200",
    product_only: "border-amber-500/40 bg-amber-500/10 text-amber-200",
    mixed: "border-sky-500/40 bg-sky-500/10 text-sky-200",
};

const RISK_STYLE = {
    low: "border-emerald-500/40 bg-emerald-500/10 text-emerald-200",
    medium: "border-amber-500/40 bg-amber-500/10 text-amber-200",
    high: "border-rose-500/40 bg-rose-500/10 text-rose-200",
};

const REF_MODE_STYLE = {
    text_only_no_reference_passed: "border-rose-500/40 bg-rose-500/10 text-rose-200",
    text_reference_id_only: "border-amber-500/40 bg-amber-500/10 text-amber-200",
    image_to_video_supported: "border-emerald-500/40 bg-emerald-500/10 text-emerald-200",
    single_inline_base64_reference: "border-emerald-500/40 bg-emerald-500/10 text-emerald-200",
};

const REF_MODE_LABEL = {
    text_only_no_reference_passed: "Text-only (no reference passed)",
    text_reference_id_only: "Text + reference IDs",
    image_to_video_supported: "Image-to-video supported",
    single_inline_base64_reference: "Reference image bound (1 image · base64 inline)",
};

export default function VideoVisualContractPanel({ projectId, provider = "sora-2" }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");

    const load = useCallback(async () => {
        if (!projectId) return;
        setLoading(true); setErr("");
        try {
            const { data } = await api.get(
                `/projects/${projectId}/video-visual-contract?provider=${encodeURIComponent(provider)}`,
            );
            setData(data);
        } catch (e) {
            setErr(e?.userMessage || e?.response?.data?.detail || "Failed to load visual contract");
        } finally {
            setLoading(false);
        }
    }, [projectId, provider]);

    useEffect(() => { load(); }, [load]);

    if (!data && !err) {
        return (
            <div className="card-base p-3 my-3 text-[11px] text-zinc-400 inline-flex items-center gap-2"
                data-testid="video-visual-contract-panel">
                <Film className="w-3.5 h-3.5 text-violet-300" />
                {loading ? "Loading video visual contract…" : "Visual contract will appear after storyboard generation."}
            </div>
        );
    }

    if (err) {
        return (
            <div className="card-base p-3 my-3 text-[11px] text-rose-300 inline-flex items-center gap-2"
                data-testid="video-visual-contract-panel">
                <AlertTriangle className="w-3.5 h-3.5" />
                {err}
            </div>
        );
    }

    const rows = data.rows || [];
    const totals = data.totals || {};
    const coverageWarnings = data.coverage_warnings || [];

    return (
        <div className="card-base p-4 my-3 space-y-3"
            data-testid="video-visual-contract-panel">
            <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                    <Film className="w-4 h-4 text-violet-300" />
                    <div className="text-sm font-semibold text-zinc-100">
                        Video Visual Contract
                    </div>
                    <span className="text-[11px] text-zinc-500">
                        {provider} · {rows.length} shot{rows.length === 1 ? "" : "s"}
                    </span>
                </div>
                <button onClick={load}
                    className="text-zinc-500 hover:text-zinc-200"
                    data-testid="video-visual-contract-refresh">
                    <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
                </button>
            </div>

            {/* Honesty disclaimer */}
            <div className="rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-[11px] text-amber-200 flex items-start gap-2"
                data-testid="video-visual-contract-disclaimer">
                <ShieldOff className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                <div>{data.honesty_disclaimer}</div>
            </div>

            {/* Totals strip */}
            {rows.length > 0 && (
                <div className="grid grid-cols-3 gap-2 text-[11px]"
                    data-testid="video-visual-contract-totals">
                    <Pill label="Storyboard target" value={`${totals.storyboard_target_duration || 0}s`} />
                    <Pill label="Provider total" value={`${totals.provider_duration || 0}s`} />
                    <Pill label="Δ duration"
                        value={`${(totals.duration_delta || 0) >= 0 ? "+" : ""}${totals.duration_delta || 0}s`}
                        emphasis={Math.abs(totals.duration_delta || 0) > 0} />
                </div>
            )}

            {coverageWarnings.length > 0 && (
                <div className="rounded-md border border-rose-500/30 bg-rose-500/5 px-3 py-2 text-[11px] text-rose-200"
                    data-testid="video-visual-contract-coverage-warnings">
                    <div className="font-semibold mb-1">Coverage gaps detected:</div>
                    <ul className="space-y-0.5">
                        {coverageWarnings.map((w, i) => (
                            <li key={i}>· {w.shot_id}: {w.note}</li>
                        ))}
                    </ul>
                </div>
            )}

            {/* Rows */}
            <div className="space-y-2" data-testid="video-visual-contract-rows">
                {rows.map((r) => (
                    <ContractRow key={r.shot_id} row={r} />
                ))}
                {rows.length === 0 && !loading && (
                    <div className="text-[11px] text-zinc-500 italic">
                        No storyboard shots yet. Run the Storyboard stage first.
                    </div>
                )}
            </div>
        </div>
    );
}

function Pill({ label, value, emphasis }) {
    return (
        <div className="rounded-md border border-zinc-800 bg-zinc-950/40 px-2 py-1.5">
            <div className="text-[9px] uppercase tracking-wide text-zinc-500">{label}</div>
            <div className={`text-sm font-semibold tabular-nums ${emphasis ? "text-amber-300" : "text-zinc-100"}`}>
                {value}
            </div>
        </div>
    );
}

function ContractRow({ row }) {
    return (
        <div className="rounded-lg border border-zinc-800 bg-zinc-950/40 p-2.5 space-y-2"
            data-testid={`visual-contract-row-${row.shot_id}`}>
            <div className="flex items-start gap-3">
                {/* Storyboard image vs paired clip */}
                <div className="flex gap-1.5 shrink-0">
                    {row.storyboard_image_path ? (
                        <div className="w-16 h-10 rounded-sm overflow-hidden bg-zinc-900 border border-zinc-800">
                            <img src={row.storyboard_image_path}
                                alt={`SB ${row.shot_id}`}
                                className="w-full h-full object-cover"
                                onError={(e) => { e.target.style.opacity = 0.2; }}
                                data-testid={`visual-contract-sb-img-${row.shot_id}`} />
                        </div>
                    ) : (
                        <div className="w-16 h-10 rounded-sm bg-zinc-900 border border-zinc-800 flex items-center justify-center">
                            <ImageIcon className="w-3 h-3 text-zinc-700" />
                        </div>
                    )}
                    {row.paired_clip ? (
                        <div className="w-16 h-10 rounded-sm overflow-hidden bg-zinc-900 border border-emerald-500/30">
                            {row.paired_clip.thumbnail_storage_path ? (
                                <img src={row.paired_clip.thumbnail_storage_path}
                                    alt={`Clip ${row.shot_id}`}
                                    className="w-full h-full object-cover"
                                    data-testid={`visual-contract-clip-img-${row.shot_id}`} />
                            ) : (
                                <div className="w-full h-full flex items-center justify-center text-emerald-300 text-[9px]">CLIP</div>
                            )}
                        </div>
                    ) : (
                        <div className="w-16 h-10 rounded-sm bg-zinc-900 border border-dashed border-zinc-700 flex items-center justify-center text-zinc-600 text-[9px]">
                            no clip
                        </div>
                    )}
                </div>

                {/* Identity + chips */}
                <div className="flex-1 min-w-0 space-y-1">
                    <div className="flex items-center justify-between gap-2">
                        <div className="text-[12px] font-semibold text-zinc-100 truncate">
                            <span className="text-zinc-500">{row.shot_order}.</span> {row.shot_id}
                            <span className="text-zinc-500 text-[10px]"> · {row.scene_id}</span>
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-1">
                        <Chip cls={VISUAL_TYPE_STYLE[row.visual_type] || VISUAL_TYPE_STYLE.character_led}
                            testId={`chip-visual-type-${row.shot_id}`}>
                            {row.visual_type}
                        </Chip>
                        <Chip cls={RISK_STYLE[row.continuity_risk] || RISK_STYLE.medium}
                            testId={`chip-risk-${row.shot_id}`}>
                            Risk: {row.continuity_risk}
                        </Chip>
                        <Chip cls={REF_MODE_STYLE[row.provider_reference_mode] || REF_MODE_STYLE.text_only_no_reference_passed}
                            testId={`chip-ref-mode-${row.shot_id}`}>
                            {REF_MODE_LABEL[row.provider_reference_mode] || row.provider_reference_mode}
                        </Chip>
                        <Chip cls="border-zinc-700 bg-zinc-900 text-zinc-300"
                            testId={`chip-duration-${row.shot_id}`}>
                            {row.storyboard_target_duration}s → {row.provider_duration}s
                            {row.duration_delta !== 0 && (
                                <span className="text-amber-300 ml-1">
                                    ({row.duration_delta > 0 ? "+" : ""}{row.duration_delta})
                                </span>
                            )}
                        </Chip>
                    </div>
                    <div className="text-[11px] text-zinc-400 truncate"
                        title={row.description}>
                        {row.description || "—"}
                    </div>
                </div>
            </div>

            {/* Locks + required + forbidden + checklist */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[11px]">
                {/* Locks */}
                <div className="rounded-md border border-zinc-800 bg-zinc-950/40 p-2">
                    <div className="text-[10px] uppercase tracking-wide text-zinc-500 mb-1">Locks</div>
                    <div className="space-y-0.5">
                        <LockLine icon={User2} label="Character"
                            value={row.character_ids?.join(", ") || "—"} />
                        <LockLine icon={MapPin} label="Environment"
                            value={row.environment_id || "—"} />
                        <LockLine icon={Package} label="Product"
                            value={row.product_asset_ids?.join(", ") || "—"} />
                    </div>
                </div>

                {/* Forbidden + Required */}
                <div className="rounded-md border border-zinc-800 bg-zinc-950/40 p-2 space-y-1">
                    <div className="text-[10px] uppercase tracking-wide text-zinc-500">Subjects</div>
                    {row.required_subjects?.length > 0 && (
                        <div className="text-emerald-300/80">
                            ✓ Required: {row.required_subjects.join(", ")}
                        </div>
                    )}
                    {row.forbidden_subjects?.length > 0 && (
                        <div className="text-rose-300/80">
                            ✗ Forbidden: {row.forbidden_subjects.join(", ")}
                        </div>
                    )}
                    {row.required_subjects?.length === 0 && row.forbidden_subjects?.length === 0 && (
                        <div className="text-zinc-500 italic">None enforced</div>
                    )}
                </div>
            </div>

            {/* Warnings */}
            {row.warnings?.length > 0 && (
                <div className="rounded-md border border-amber-500/30 bg-amber-500/5 px-2 py-1.5 text-[11px] text-amber-200"
                    data-testid={`visual-contract-warnings-${row.shot_id}`}>
                    <ul className="space-y-0.5">
                        {row.warnings.map((w, i) => (
                            <li key={i}>⚠ {w}</li>
                        ))}
                    </ul>
                </div>
            )}

            {/* Checklist */}
            {row.reference_checklist?.length > 0 && (
                <details className="text-[11px]">
                    <summary className="cursor-pointer text-zinc-400 hover:text-zinc-200"
                        data-testid={`visual-contract-checklist-toggle-${row.shot_id}`}>
                        Reference checklist ({row.reference_checklist.length})
                    </summary>
                    <ul className="mt-1 space-y-0.5 pl-3">
                        {row.reference_checklist.map((c, i) => (
                            <li key={i} className="text-zinc-300">
                                <span className="text-zinc-500">[{c.kind}]</span> {c.asset}
                                {c.required_for ? <span className="text-zinc-500"> — {c.required_for}</span> : null}
                            </li>
                        ))}
                    </ul>
                </details>
            )}
        </div>
    );
}

function Chip({ cls, children, testId }) {
    return (
        <span className={`px-1.5 py-0.5 rounded-sm border text-[10px] inline-flex items-center gap-1 ${cls}`}
            data-testid={testId}>
            {children}
        </span>
    );
}

function LockLine({ icon: Icon, label, value }) {
    return (
        <div className="flex items-center gap-1.5 text-zinc-300">
            <Icon className="w-3 h-3 text-zinc-500" />
            <span className="text-zinc-500 w-20 shrink-0">{label}:</span>
            <span className="truncate">{value}</span>
        </div>
    );
}
